import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api } from '../lib/api'
import { useFeatureFlags } from '../lib/features'
import { NotFound } from '../components/NotFound'
import { useMessages } from '@/i18n/messages'
import { umsoknMessages } from '@/i18n/text/umsokn'

export const Route = createFileRoute('/umsokn')({
  component: AccessRequestPage,
})

const inputClass =
  'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none'

const initial = {
  companyKennitala: '',
  companyName: '',
  applicantKennitala: '',
  applicantName: '',
  applicantEmail: '',
  applicantPhone: '',
  approverName: '',
  approverEmail: '',
}

function AccessRequestPage() {
  const m = useMessages(umsoknMessages)
  const flags = useFeatureFlags()
  const [form, setForm] = useState(initial)
  const [website, setWebsite] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  function set<K extends keyof typeof initial>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const selfApproved =
    form.approverEmail.trim() !== '' &&
    form.approverEmail.trim().toLowerCase() === form.applicantEmail.trim().toLowerCase()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await api.accessRequests.create({ ...form, website })
      setSent(true)
    } catch (err) {
      setError((err as Error).message || m.submitFailed)
    } finally {
      setLoading(false)
    }
  }

  if (flags.isLoading) return null
  if (!flags.accessRequests) return <NotFound />

  if (sent) {
    return (
      <div className="max-w-xl mx-auto px-6 py-16">
        <h1 className="text-2xl font-bold text-slate-900">{m.sentTitle}</h1>
        <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
          <p>
            {m.sentToBefore} <strong>{form.approverName}</strong> {m.sentToAfter(form.approverEmail)}
          </p>
          <p className="mt-2">
            {m.sentNext}
          </p>
        </div>
        <Link to="/login" className="mt-6 inline-block text-sm text-emerald-700 underline hover:text-emerald-900">
          {m.backToLogin}
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-16">
      <h1 className="text-2xl font-bold text-slate-900">{m.title}</h1>
      <div className="mt-3 space-y-2 text-sm text-slate-500">
        <p>{m.intro1}</p>
        <p>{m.intro2}</p>
        <p>{m.intro3}</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-slate-900">{m.legendCompany}</legend>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.companyKennitala}</label>
            <input
              required
              inputMode="numeric"
              value={form.companyKennitala}
              onChange={(e) => set('companyKennitala', e.target.value)}
              placeholder="000000-0000"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">
              {m.companyName} <span className="font-normal text-slate-400">{m.optional}</span>
            </label>
            <input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
              className={inputClass}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-slate-900">{m.legendYou}</legend>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.yourKennitala}</label>
            <input
              required
              inputMode="numeric"
              value={form.applicantKennitala}
              onChange={(e) => set('applicantKennitala', e.target.value)}
              placeholder="000000-0000"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.yourName}</label>
            <input
              required
              value={form.applicantName}
              onChange={(e) => set('applicantName', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.yourEmail}</label>
            <input
              required
              type="email"
              value={form.applicantEmail}
              onChange={(e) => set('applicantEmail', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.yourPhone}</label>
            <input
              required
              inputMode="tel"
              value={form.applicantPhone}
              onChange={(e) => set('applicantPhone', e.target.value)}
              className={inputClass}
            />
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-sm font-semibold text-slate-900">{m.legendApprover}</legend>
          <p className="text-sm text-slate-500">
            {m.approverHelp}
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.approverName}</label>
            <input
              required
              value={form.approverName}
              onChange={(e) => set('approverName', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{m.approverEmail}</label>
            <input
              required
              type="email"
              value={form.approverEmail}
              onChange={(e) => set('approverEmail', e.target.value)}
              className={inputClass}
            />
          </div>
          {selfApproved && (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {m.selfApproved}
            </p>
          )}
        </fieldset>

        <label className="hidden" aria-hidden>
          {m.honeypot}
          <input
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded bg-emerald-600 px-4 py-2 font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {loading ? m.submitting : m.submit}
        </button>

        <div className="text-center">
          <Link to="/login" className="text-sm text-emerald-700 underline hover:text-emerald-900">
            {m.backToLogin}
          </Link>
        </div>
      </form>
    </div>
  )
}
